import React from 'react'
import {generatePath} from 'react-router'
import {Link as RouterLink} from 'react-router-dom'
import {CompartmentLink} from '../../../common/router_links/CompartmentLink'
import {Routes} from '../../../common/Routes'
import {Compartment} from '../../../common/types/compartment'
import {toDisplayDateTime} from '../../../common/utils/dateUtils'
import {resolveProductStatusDisplayName} from '../../../common/utils/resolveProductStatusDisplayName'
import {Link} from '../../../webui/Link'
import {SortablePaginatedTable} from '../../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../../webui/table/sortable-paginated-table/TableHeadCell'
import {getNumericalComparator} from '../../../webui/table/sortable-paginated-table/utils'
import {CurrentStatusData} from './types'

interface CurrentStatusTableProps {
  boxId: string
  boxName: string
  compartments: Compartment[]
}

const createTableHeadCells = (boxId: string): TableHeadCell<CurrentStatusData>[] => [
  {
    id: 'name',
    label: 'Name',
    render: (value) => <CompartmentLink boxId={boxId} compartmentId={value.id} linkText={value.name} />,
  },
  {
    id: 'circuitBoardPort',
    label: 'Port nr',
    render: (value) => value['circuitBoardPort'],
  },
  {
    id: 'product',
    label: 'Product',
    render: (value) => value['product'],
  },
  {
    id: 'rentingBy',
    label: 'Currently rented by',
    render: (value) => value['rentingBy'],
  },
  {
    id: 'numberOfRentals',
    label: 'Number of rentals',
    render: (value) => value['numberOfRentals'],
  },
  {
    id: 'latestRental',
    label: 'Latest rental',
    render: (value) =>
      value.latestRentalId ? (
        <Link component={RouterLink} to={generatePath(Routes.RENTAL, {id: value.latestRentalId})} underline="hover">
          {value.latestRental}
        </Link>
      ) : (
        value.latestRental
      ),
  },
  {
    id: 'productStatus',
    label: 'Product status',
    render: (value) => (value['productStatus'] ? resolveProductStatusDisplayName(value['productStatus']) : ''),
  },
  {
    id: 'locked',
    label: '',
    render: (value) => <>{value['locked'] ? 'Locked' : ''}</>,
  },
  {
    id: 'private',
    label: '',
    render: (value) => <>{value['private'] ? 'Private' : ''}</>,
  },
]

const createRows = (compartments: Compartment[]): CurrentStatusData[] => {
  return compartments.map((compartment) => {
    const ongoingRental = compartment.ongoingRental
    const numberOfRentals = compartment.rentalsCount ?? (ongoingRental ? 1 : 0)
    const latestRentalDate = compartment.latestRental?.rentStart ?? ongoingRental?.rentStart

    return {
      id: compartment.id,
      locked: compartment.locked ?? false,
      name: compartment.name || '__no_name__',
      product: compartment.product?.name ?? 'No product',
      rentingBy: ongoingRental?.rentBy.email ?? '',
      numberOfRentals,
      latestRental: toDisplayDateTime(latestRentalDate) ?? '',
      latestRentalId: compartment.latestRental?.id ?? ongoingRental?.id,
      productStatus: compartment.product?.productStatus,
      rating: 0,
      private: compartment.private,
      circuitBoardPort: compartment.circuitBoardPort,
    }
  })
}

const INITIAL_ROWS_PER_PAGE = 14

export const CurrentStatusTable: React.FC<CurrentStatusTableProps> = ({boxId, boxName, compartments}) => {
  return (
    <SortablePaginatedTable
      title={boxName}
      titleLinkUrl={generatePath(Routes.BOX, {id: boxId})}
      initialRowsPerPage={INITIAL_ROWS_PER_PAGE}
      initialOrderByKey={'name'}
      rows={createRows(compartments)}
      tableHeadCells={createTableHeadCells(boxId)}
      getComparator={getNumericalComparator}
    />
  )
}
